﻿import { useTranslation } from "react-i18next";
import TopBar from "@/pages/home/components/TopBar";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import StickyMobile from "@/pages/home/components/StickyMobile";

function Breadcrumb() {
  return (
    <nav aria-label="Kruimelpad" className="bg-white border-b border-brand-border">
      <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-3">
        <ol className="flex items-center gap-2 text-sm font-geist text-brand-text/50">
          <li>
            <a href="/" className="hover:text-brand-blue transition-colors">Home</a>
          </li>
          <li className="flex items-center gap-2">
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-xs"></i>
            <span className="text-brand-text">Algemene Voorwaarden</span>
          </li>
        </ol>
      </div>
    </nav>
  );
}

function SchemaMarkup() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Algemene Voorwaarden — Ramen Repair Eindhoven",
    description: "Algemene voorwaarden voor kozijn-, raam-, schuifpui- en glasreparatiediensten.",
    url: "https://ramen-repair-eindhoven.nl/algemene-voorwaarden/",
    isPartOf: {
      "@type": "WebSite",
      name: "Ramen Repair Eindhoven",
      url: "https://ramen-repair-eindhoven.nl/",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://ramen-repair-eindhoven.nl/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Algemene Voorwaarden",
        item: "https://ramen-repair-eindhoven.nl/algemene-voorwaarden/",
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </>
  );
}

export default function AlgemeneVoorwaarden() {
  const { t } = useTranslation("algemene-voorwaarden");

  return (
    <main className="bg-white min-h-screen">
      <title>{t("seo.title")}</title>
      <meta name="description" content={t("seo.description")} />
      <meta property="og:title" content={t("seo.title")} />
      <meta property="og:description" content={t("seo.description")} />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://ramen-repair-eindhoven.nl/algemene-voorwaarden/" />
      <meta property="og:image" content="/og/og-home.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Ramen Repair Eindhoven - kozijn en raamreparatie" />
      <link rel="canonical" href="https://ramen-repair-eindhoven.nl/algemene-voorwaarden/" />
      <link rel="alternate" hreflang="nl-NL" href="https://ramen-repair-eindhoven.nl/algemene-voorwaarden/" />
      <link rel="alternate" hreflang="x-default" href="https://ramen-repair-eindhoven.nl/algemene-voorwaarden/" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t("seo.title")} />
      <meta name="twitter:description" content={t("seo.description")} />
      <meta name="twitter:image" content="/og/og-home.jpg" />
      <SchemaMarkup />
      <TopBar />
      <Header />
      <Breadcrumb />

      <section className="max-w-[720px] mx-auto px-4 md:px-6 py-16 md:py-24">
        <h1 className="font-geist font-bold text-3xl md:text-4xl lg:text-5xl text-brand-text tracking-tight mb-4">
          {t("seo.h1")}
        </h1>
        <p className="font-geist text-sm text-brand-text/50 mb-6">
          {t("version")}
        </p>

        <div className="bg-brand-background border border-brand-border rounded-2xl p-6 md:p-8 mb-12">
          <p className="font-geist font-semibold text-sm text-brand-text mb-4">
            Ondernemingsgegevens
          </p>
          <div className="flex flex-col gap-2 font-geist text-sm text-brand-text/70">
            <p>
              <span className="text-brand-text/50">Handelsnaam:</span>{" "}
              <strong className="text-brand-text font-semibold">Ramen Repair</strong>
            </p>
            <p>
              <span className="text-brand-text/50">Rechtspersoon:</span> DFPRO
            </p>
            <p>
              <span className="text-brand-text/50">Ondernemingsnummer/BTW:</span> BE 0799.088.572
            </p>
            <p>
              <span className="text-brand-text/50">Zetel:</span> Rue Lecat 11/4, 7100 La Louvière, België
            </p>
            <p>
              <span className="text-brand-text/50">E-mail:</span>{" "}
              <a href="mailto:info@ramen-repair-eindhoven.nl" className="text-brand-blue underline underline-offset-4 hover:text-brand-text transition-colors">
                info@ramen-repair-eindhoven.nl
              </a>
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-14 md:gap-16">
          {t("articles", { returnObjects: true }).map((art: {
            nr: string;
            title: string;
            paragraphs: string[];
          }) => (
            <article key={art.nr} className="scroll-mt-24">
              <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text tracking-tight mb-4 pb-3 border-b border-brand-border">
                <span className="text-brand-yellow mr-2">{art.nr}</span>{art.title}
              </h2>
              <div className="flex flex-col gap-3">
                {art.paragraphs.map((p, i) => (
                  <p key={i} className="font-geist text-base text-brand-text/80 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>

        <p className="font-geist text-sm text-brand-text/50 mt-20 pt-6 border-t border-brand-border italic">
          {t("footer.version")}
        </p>
      </section>

      <Footer />
      <StickyMobile />
    </main>
  );
}